/* eslint-disable @next/next/no-img-element */
// HERO "Dwello" (modelo_busca = 9): hero claro, editorial e arejado.
//  - ESQUERDA: headline GRANDE em duas linhas, subtítulo curto e um CTA escuro
//    arredondado (link real p/ a listagem — nada de scroll simulado);
//  - DIREITA: banner "topo" do tenant num contêiner de cantos bem arredondados
//    com sombra suave (sem banner → bloco em degradê da paleta, nunca quebra);
//  - SOBREPOSTO na base: card de busca bege com campos claros (Localização,
//    Tipo e faixa de preço) + botão escuro "Buscar";
//  - ABAIXO: faixa de estatísticas do próprio tenant (imóveis, cidades, VGV) —
//    cada tile só existe se o dado existir; NADA hardcoded.
// Busca (estado + URL + tracking Meta) vem inteira do hook compartilhado
// `useHeroSearch` — este componente só desenha os controles.
// Cores 100% tokenizadas (var(--corN, #fallback)); onde o fundo é cor do tenant,
// a cor do texto é CALCULADA (isColorLight + ensureReadableTextColor).
// Default export = ErrorBoundary com fallback pro hero padrão (padrão WelcomeReserva).
import React from 'react';
import { ensureReadableTextColor, isColorLight } from '../../../utils/FunctionsGlobals';
import type { HeroSectionProps } from '../../registry/types';
import { useHeroSearch } from '../../shared/hero-search/useHeroSearch';
import DrawerComponent from '../../drawer/DrawerComponent';
import CardsBusca from '../../shared/cards-busca/CardsBusca';
import { getHeroVideoPoster, useHeroVideoReady } from '../../../utils/heroVideo';
import styles from './WelcomeDwello.module.css';
import { usePreviaBuilder } from '../../../lib/previaBuilder';

const stripHtml = (value: unknown): string =>
    String(value ?? '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

// Milhar com ponto SEM Intl: toLocaleString depende do ICU do runtime e pode
// divergir entre servidor e navegador → hydration mismatch (React #418/#423).
const formatarNumero = (value: number): string =>
    String(Math.trunc(value)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');

// Só vira tile se o dado REALMENTE existir (> 0 / string não vazia).
const numeroValido = (value: unknown): number => {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : 0;
};

const WelcomeDwelloInner: React.FC<HeroSectionProps> = ({
    allCities,
    allTypes,
    allTransacao,
    site,
    urlBanner,
    origin,
    codsite2,
    setActiveDrawer,
    activeDrawer,
    favorito,
    props,
    allCards,
}) => {
    const { videoParado } = usePreviaBuilder(); // prévia dos cards do CRM: vídeo parado na capa
    const {
        transacoes,
        transacao,
        setTransacao,
        cidade,
        setCidade,
        tipo,
        setTipo,
        precoMin,
        setPrecoMin,
        precoMax,
        setPrecoMax,
        loading,
        onSubmit,
        verImoveisUrl,
    } = useHeroSearch({ site, allCities, allTypes, allTransacao, codsite2, origin });

    // ---- cores: papéis da paleta do catálogo; o hex do CSS é só a rede de segurança ----
    const fundo = site?.cor5 || '#fef7f2';
    const corTexto =
        ensureReadableTextColor(isColorLight(fundo) ? site?.cor11 : site?.cor13, fundo) || '#2b1b12';

    const acao = site?.cor2 || '#2b1b12';
    const corAcaoTexto =
        ensureReadableTextColor(isColorLight(acao) ? site?.cor11 : site?.cor13, acao) || '#ffffff';

    const cardBusca = site?.cor15 || '#ddc7bb';
    const corCardTexto =
        ensureReadableTextColor(isColorLight(cardBusca) ? site?.cor11 : site?.cor13, cardBusca) ||
        '#2b1b12';

    const campo = site?.cor12 || '#fbf5f1';
    const corCampoTexto =
        ensureReadableTextColor(isColorLight(campo) ? site?.cor11 : site?.cor13, campo) || '#2b1b12';

    // ---- conteúdo do tenant ----
    const bannerSrc = urlBanner && urlBanner !== 'undefined' ? String(urlBanner).replaceAll('"', '') : '';
    // Mídia da figura: vídeo do tenant > banner > fallback (mesma prioridade dos heros 1/2/3).
    const videoSrc = site?.url_video ? String(site.url_video).replaceAll('"', '') : '';
    const heroPoster = getHeroVideoPoster(site?.url_video);
    const heroVideoReady = useHeroVideoReady();
    const headline = site?.frase_busca
        ? stripHtml(site.frase_busca)
        : 'Encontre o imóvel dos seus sonhos';
    const subtitle = site?.frase_busca_2
        ? stripHtml(site.frase_busca_2)
        : 'Uma seleção cuidadosa de imóveis para você encontrar o endereço certo, do seu jeito.';

    const moeda = site?.moeda_tipo || 'R$';

    const totalImoveis = numeroValido(site?.total_imoveis);
    const totalCidades = numeroValido(site?.total_cidades);
    const vgv = stripHtml(site?.vgv_total);
    const stats = [
        totalImoveis ? { key: 'imoveis', valor: formatarNumero(totalImoveis), rotulo: 'Imóveis disponíveis' } : null,
        totalCidades ? { key: 'cidades', valor: formatarNumero(totalCidades), rotulo: 'Cidades atendidas' } : null,
        vgv ? { key: 'vgv', valor: vgv, rotulo: 'VGV negociado' } : null,
    ].filter(Boolean) as Array<{ key: string; valor: string; rotulo: string }>;

    return (
        <section className={styles.dwello} style={{ background: fundo }}>
            <DrawerComponent
                site={site}
                activeDrawer={activeDrawer}
                setActiveDrawer={setActiveDrawer}
                favorito={favorito || []}
                menus={props?.menus}
            />

            <div className={styles.inner}>
                {/* ---------- topo: texto + imagem ---------- */}
                <div className={styles.hero}>
                    <div className={styles.copy}>
                        <h1 className={styles.headline} style={{ color: corTexto }}>
                            {headline}
                        </h1>
                        <p className={styles.subtitle} style={{ color: corTexto }}>
                            {subtitle}
                        </p>
                        <a
                            href={verImoveisUrl}
                            className={styles.cta}
                            style={{ background: acao, color: corAcaoTexto }}
                        >
                            Ver imóveis
                        </a>
                    </div>

                    <div className={styles.figure}>
                        {videoSrc ? (
                            <video
                                className={styles.figureImg}
                                autoPlay={!videoParado}
                                muted
                                loop
                                playsInline
                                preload="none"
                                poster={heroPoster}
                                src={heroPoster ? (heroVideoReady ? videoSrc : undefined) : videoSrc}
                            >
                                <track kind="captions" label="Legenda" src="legenda.vtt" />
                            </video>
                        ) : bannerSrc ? (
                            <img
                                className={styles.figureImg}
                                src={bannerSrc}
                                alt=""
                                decoding="async"
                            />
                        ) : (
                            <div className={styles.figureFallback} aria-hidden="true" />
                        )}
                    </div>
                </div>

                {/* ---------- card de busca sobreposto ---------- */}
                {/* id="myLink": âncora do botão "BUSCA AVANÇADA" do topo (topo2). */}
                <form
                    id="myLink"
                    className={styles.searchCard}
                    style={{ background: cardBusca }}
                    onSubmit={onSubmit}
                >
                    {transacoes.length > 1 ? (
                        <div className={styles.transacaoRow} role="group" aria-label="Transação">
                            {transacoes.map((t) => {
                                const value = String(t.value).toLowerCase();
                                const ativo = value === transacao;
                                return (
                                    <button
                                        key={value}
                                        type="button"
                                        className={styles.transacaoChip}
                                        aria-pressed={ativo}
                                        onClick={() => setTransacao(value)}
                                        style={
                                            ativo
                                                ? { background: acao, color: corAcaoTexto, borderColor: acao }
                                                : { color: corCardTexto, borderColor: corCardTexto }
                                        }
                                    >
                                        {t.label}
                                    </button>
                                );
                            })}
                        </div>
                    ) : null}

                    <div className={styles.fields}>
                        <label className={styles.field} style={{ background: campo }}>
                            <span className={styles.fieldLabel} style={{ color: corCampoTexto }}>
                                Localização
                            </span>
                            <select
                                className={styles.select}
                                style={{ color: corCampoTexto }}
                                value={cidade}
                                onChange={(e) => setCidade(e.target.value)}
                                aria-label="Localização"
                            >
                                <option value="">Todas as cidades</option>
                                {(allCities || []).map((c: { label: string; value: any }) => (
                                    <option key={String(c.value)} value={String(c.value)}>
                                        {c.label}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <label className={styles.field} style={{ background: campo }}>
                            <span className={styles.fieldLabel} style={{ color: corCampoTexto }}>
                                Tipo
                            </span>
                            <select
                                className={styles.select}
                                style={{ color: corCampoTexto }}
                                value={tipo}
                                onChange={(e) => setTipo(e.target.value)}
                                aria-label="Tipo de imóvel"
                            >
                                <option value="">Todos os tipos</option>
                                {(allTypes || []).map((t: { label: string; value: any }) => (
                                    <option key={String(t.value)} value={String(t.value)}>
                                        {t.label}
                                    </option>
                                ))}
                            </select>
                        </label>

                        <div className={styles.field} style={{ background: campo }}>
                            <span className={styles.fieldLabel} style={{ color: corCampoTexto }}>
                                Faixa de preço ({moeda})
                            </span>
                            <div className={styles.priceRow}>
                                <input
                                    className={styles.input}
                                    style={{ color: corCampoTexto }}
                                    type="number"
                                    inputMode="numeric"
                                    min={0}
                                    placeholder="Mín."
                                    value={precoMin}
                                    onChange={(e) => setPrecoMin(e.target.value)}
                                    aria-label="Preço mínimo"
                                />
                                <span className={styles.priceSep} style={{ background: corCampoTexto }} aria-hidden="true" />
                                <input
                                    className={styles.input}
                                    style={{ color: corCampoTexto }}
                                    type="number"
                                    inputMode="numeric"
                                    min={0}
                                    placeholder="Máx."
                                    value={precoMax}
                                    onChange={(e) => setPrecoMax(e.target.value)}
                                    aria-label="Preço máximo"
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            className={styles.submit}
                            style={{ background: acao, color: corAcaoTexto }}
                            disabled={loading}
                        >
                            {loading ? 'Buscando...' : 'Buscar'}
                        </button>
                    </div>
                </form>

                {/* ---------- faixa de estatísticas ---------- */}
                {stats.length ? (
                    <div className={styles.stats}>
                        {stats.map((s) => (
                            <div key={s.key} className={styles.stat}>
                                <strong className={styles.statValue} style={{ color: corTexto }}>
                                    {s.valor}
                                </strong>
                                <span className={styles.statLabel} style={{ color: corTexto }}>
                                    {s.rotulo}
                                </span>
                            </div>
                        ))}
                    </div>
                ) : null}

                <CardsBusca cards={allCards} site={site} variante="claro" />
            </div>
        </section>
    );
};

class WelcomeDwello extends React.Component<HeroSectionProps, { hasError: boolean }> {
    constructor(props: HeroSectionProps) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch() {
        // silencioso: degrada pro fallback sem quebrar a home.
    }
    render() {
        const { fallback, site } = this.props;
        if (this.state.hasError || !site) return fallback ?? null;
        return <WelcomeDwelloInner {...this.props} />;
    }
}

export default WelcomeDwello;
